/**
 * Dashboard shell: responsive sidebar + header + outlet + footer.
 *
 * - Desktop (md+): collapsible rail (w-60 ↔ w-16); collapsed shows icon + tooltip.
 * - Mobile (<md): off-canvas drawer opened by the header hamburger (overlay + backdrop).
 * - Header: command-palette trigger, theme toggle, user dropdown (Settings + Déconnexion).
 * - Footer: product name, version, runtime env indicator, status dot.
 *
 * Fully themed via the semantic design tokens (bg/surface/border/…) so light
 * and dark are driven by the `.dark` class the UI store toggles.
 */

import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation, useMatches, useNavigate } from 'react-router';
import { pageKeyOf } from './pageKey';
import BrandLogo from '@/components/BrandLogo';
import { useAuth } from 'react-oidc-context';

import { useT, type TranslationKey } from '@/i18n';
import {
  ALL_HUB_ITEMS,
  ALL_NAV_TARGETS,
  NAV_GROUPS,
  NAV_HUBS,
  deriveBreadcrumb,
  flattenHubItems,
  flattenNavItems,
  getNavItem,
  isNavRouteActive,
  matchNavItem,
  type IconName,
  type NavHub,
  type NavItem,
} from '@/app/layouts/navCatalog';
import { useNavAccess } from '@/app/layouts/useNavAccess';
import { applyWorkspacePreset } from '@/app/layouts/workspacePresets';
import { useNavGroupExpansion } from '@/app/layouts/useNavGroupExpansion';
import { useWorkspacePresetStore } from '@/stores/workspace-preset-store';
import { WorkspaceSwitcher } from '@/app/layouts/WorkspaceSwitcher';
import { AccountSwitcher } from '@/app/layouts/AccountSwitcher';
import { ActingTenantBanner } from '@/app/layouts/ActingTenantBanner';
import { clearActingTenant } from '@/app/providers/ActingTenantProvider';
import { useNavStore } from '@/stores/nav-store';
import { CommandPalette, findCommandLabel } from '@/app/CommandPalette';
import {
  ShortcutsOverlay,
  getFocusable,
  nextTrapFocus,
  useGlobalShortcuts,
} from '@/app/keyboard-shortcuts';
import { RouteErrorBoundary } from '@/app/RouteErrorBoundary';
import { UpdateBanner } from '@/app/UpdateBanner';
import { getConfig } from '@/config';
import { CopilotProvider } from '@/features/copilot';
import { AiCreditsBadge, CreditGuardHost } from '@/features/credits';
import { PlanRequiredHost } from '@/features/entitlements';
import { OutOfCreditsRequestMoreHost } from '@/features/credits/RequestMoreCreditsModal';
import { DemoTour } from '@/features/demo/DemoTour';
import { OnboardingGate } from '@/routes/onboarding/OnboardingWizard';
import { NotificationBell } from '@/routes/notifications/NotificationBell';
import { SupportButton } from '@/features/support/SupportButton';
import { useUIStore } from '@/stores/ui-store';
import { GlobalConfirmHost } from '@/ui';
import { ThemePicker } from '@/ui/ThemePicker';
import { LanguageSwitcher } from '@/i18n/LanguageSwitcher';
import { useProfileLocaleSync } from '@/i18n/profileLocale';
import { Toaster } from '@/ui/toast';

// Build version — injected at build time (see vite.config.ts). Same across
// dev/QA/prod; full provenance at /versionsbuilt.
const APP_VERSION = __BUILD_META__.version;

// The nav model + pure helpers live in ./navCatalog (re-exported here so the
// historical import path `@/app/layouts/DashboardLayout` keeps working).
export { NAV_GROUPS, NAV_HUBS, deriveBreadcrumb, flattenNavItems, isNavRouteActive };
export type { Crumb } from '@/app/layouts/navCatalog';

// ---------------------------------------------------------------------------
// Inline SVG icons (zero-dep). Common 24x24 stroke paths, sized by className.
// ---------------------------------------------------------------------------

const ICON_PATHS: Record<IconName, ReactNode> = {
  dashboard: <path d="M4 4h7v7H4zM13 4h7v4h-7zM13 10h7v10h-7zM4 13h7v7H4z" />,
  content: <path d="M8 4h8l4 4v12H4V4zM14 4v4h4M8 13h8M8 17h5" />,
  calendar: <path d="M4 5h16v15H4zM4 9h16M8 3v4M16 3v4" />,
  publishing: <path d="m4 12 16-8-6 16-3-6-7-2z" />,
  platforms: <path d="M7 12a5 5 0 0 1 5-5m0 10a5 5 0 0 0 5-5M6 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM18 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM12 22a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />,
  media: <path d="M4 5h16v14H4zM4 15l4-4 5 5M14 13l2-2 4 4M9 9a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />,
  analytics: <path d="M5 20V10M12 20V4M19 20v-7M3 20h18" />,
  design: <path d="M12 3a9 9 0 1 0 0 18h1.5a2 2 0 0 0 1.6-3.2 2 2 0 0 1 1.6-3.2H19a3 3 0 0 0 3-3A9 9 0 0 0 12 3zM7.5 11.5a1 1 0 1 1 0-.01M12 8a1 1 0 1 1 0-.01M16 11a1 1 0 1 1 0-.01" />,
  workflows: <path d="M6 4a2 2 0 1 1 0 4 2 2 0 0 1 0-4zM18 16a2 2 0 1 1 0 4 2 2 0 0 1 0-4zM6 8v6a2 2 0 0 0 2 2h8" />,
  crm: <path d="M16 20v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2M9.5 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM21 20v-2a4 4 0 0 0-3-3.9M16.5 4.1a4 4 0 0 1 0 7.8" />,
  outreach: <path d="M4 5h16v14H4zM4 6l8 6 8-6" />,
  // Email hub — an envelope with a bolder open-flap (distinct from `outreach`).
  email: <path d="M3 6h18v12H3zM3 7l9 7 9-7M3 18l6-6M21 18l-6-6" />,
  inbox: <path d="M4 4h16v16H4zM4 13h4l2 3h4l2-3h4" />,
  projects: <path d="M4 6h5l2 2h9v10H4zM4 6v12" />,
  'ai-assistant': <path d="M12 3l1.6 3.6L17 8l-3.4 1.4L12 13l-1.6-3.6L7 8l3.4-1.4zM18 14l.8 1.8L21 17l-2.2.8L18 20l-.8-2.2L15 17l2.2-.8z" />,
  // Équipe IA — a friendly agent/robot head with antenna + side sensors.
  'ai-team': <path d="M12 2v2M7 6h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2zM9 12h.01M15 12h.01M9.5 15.5h5M3 10v4M21 10v4" />,
  // Orchestration — connected nodes converging (a plan DAG fanning to a result).
  orchestration: <path d="M5 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM19 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM12 22a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM5 6v4a2 2 0 0 0 2 2h1M19 6v4a2 2 0 0 1-2 2h-1M9 12h6M12 12v6" />,
  studio: <path d="M3 9h18v10H3zM3 9l1.5-4h15L21 9M8 5 6.5 9M13 5l-1.5 4M18 5l-1.5 4" />,
  repurpose: <path d="M4 9a8 8 0 0 1 13-3l2 2M20 15a8 8 0 0 1-13 3l-2-2M17 4v4h-4M7 20v-4h4" />,
  hooks: <path d="M14 4h3v6a5 5 0 0 1-10 0M14 4v8a2 2 0 1 1-4 0" />,
  voice: <path d="M4 10v4M8 6v12M12 8v8M16 5v14M20 10v4" />,
  'ab-test': <path d="M9 3h6M10 3v6l-5 9a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-5-9V3M7 16h10" />,
  suggestions: <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-4 10c1 1 1.5 2 1.5 3h5c0-1 .5-2 1.5-3a6 6 0 0 0-4-10z" />,
  personalization: <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />,
  community: <path d="M8 10h8M8 14h5M21 12a8 8 0 0 1-11.3 7.3L3 21l1.7-6.7A8 8 0 1 1 21 12z" />,
  documents: <path d="M8 4h6l4 4v10a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM14 4v4h4M9 13h6M9 16h4" />,
  memory: <path d="M9 3a3 3 0 0 0-3 3 3 3 0 0 0-1 5.8A3 3 0 0 0 6 17a3 3 0 0 0 6 .9V6a3 3 0 0 0-3-3zM15 3a3 3 0 0 1 3 3 3 3 0 0 1 1 5.8A3 3 0 0 1 18 17a3 3 0 0 1-6 .9" />,
  approvals: <path d="M4 12a8 8 0 1 0 16 0 8 8 0 0 0-16 0zM9 12l2 2 4-4" />,
  'ai-activity': <path d="M3 12h4l2 6 4-14 2 8h6" />,
  marketplace: <path d="M4 9h16l-1-4H5zM5 9v11h14V9M9 20v-6h6v6" />,
  // Base B2B — stacked database cylinders.
  prospect: <path d="M4 6c0-1.1 3.6-2 8-2s8 .9 8 2-3.6 2-8 2-8-.9-8-2zM4 6v12c0 1.1 3.6 2 8 2s8-.9 8-2V6M4 12c0 1.1 3.6 2 8 2s8-.9 8-2" />,
  // Découverte — magnifier over a geo pin (geo-targeted discovery).
  discovery: <path d="M12 21s7-6.5 7-11.5a7 7 0 1 0-14 0C5 14.5 12 21 12 21zM9.5 9.5a2.5 2.5 0 1 0 5 0 2.5 2.5 0 0 0-5 0z" />,
  // Social leads — connected nodes.
  social: <path d="M18 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM18 22a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM8.6 10.5l6.8-3.9M8.6 13.5l6.8 3.9" />,
  // Social listening — broadcast / signal waves.
  listening: <path d="M4.9 4.9a10 10 0 0 0 0 14.2M7.8 7.8a6 6 0 0 0 0 8.4M19.1 4.9a10 10 0 0 1 0 14.2M16.2 7.8a6 6 0 0 1 0 8.4M12 13.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" />,
  // Ads — megaphone.
  ads: <path d="M3 11v2a1 1 0 0 0 1 1h2l3.5 3.5a1 1 0 0 0 1.7-.7V7.2a1 1 0 0 0-1.7-.7L6 10H4a1 1 0 0 0-1 1zM15 8a4 4 0 0 1 0 8M7 18l1 3" />,
  // Link-in-bio — chain link.
  bio: <path d="M9 12h6M8.5 8H7a4 4 0 0 0 0 8h1.5M15.5 8H17a4 4 0 0 1 0 8h-1.5" />,
  settings: <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 13a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 7 19.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.7 7l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9.4A1.6 1.6 0 0 0 11 3.1V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 2.7 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9.4a1.6 1.6 0 0 0 1.7 1.6H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z" />,
};

function NavIcon({ name, className = 'h-5 w-5' }: { name: IconName; className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {ICON_PATHS[name]}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Navigation model.
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Pure nav helpers (exported for unit tests).
// ---------------------------------------------------------------------------


// Quick-create shortcuts surfaced in the header "＋ Créer" menu.
// `?new=1` is the create-intent flag (see useCreateIntent): the target list
// page opens its own create modal on land instead of just showing the list.
const QUICK_CREATE: { labelKey: TranslationKey; to: string; icon: IconName; hintKey: TranslationKey }[] = [
  { labelKey: 'nav.quickCreate.newContent', to: '/content/new', icon: 'content', hintKey: 'nav.items.content' },
  { labelKey: 'nav.quickCreate.newContact', to: '/crm/contacts?new=1', icon: 'crm', hintKey: 'nav.items.crm' },
  { labelKey: 'nav.quickCreate.newCompany', to: '/crm/companies?new=1', icon: 'crm', hintKey: 'nav.items.crm' },
  { labelKey: 'nav.quickCreate.newCampaign', to: '/outreach/campaigns?new=1', icon: 'outreach', hintKey: 'nav.items.outreach' },
  { labelKey: 'nav.quickCreate.newProject', to: '/projects?new=1', icon: 'projects', hintKey: 'nav.items.projects' },
  { labelKey: 'nav.quickCreate.newDesign', to: '/design', icon: 'design', hintKey: 'nav.items.design' },
];

// ---------------------------------------------------------------------------
// Sidebar group collapse — every group starts collapsed on every load (see
// useNavGroupExpansion). Every collapsible key: hub ids + sub-group ids.
// ---------------------------------------------------------------------------

const ALL_COLLAPSIBLE_KEYS: string[] = NAV_HUBS.flatMap((h) => [
  h.id,
  ...(h.subGroups?.map((s) => s.id) ?? []),
]);

// ---------------------------------------------------------------------------
// Header controls.
// ---------------------------------------------------------------------------

/** Header "＋ Créer" dropdown of quick-create actions. */
function QuickCreateMenu() {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const panelId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        // Disclosure (not an APG menu widget): the panel is a plain list of
        // <button>s, so Tab moves through them and Escape closes — no role=menu /
        // menuitem (which would promise arrow-key semantics we don't implement).
        //
        // Distinct accessible name: the visible label is just "Créer", which on
        // pages like /content/new collides with that page's own "Créer" submit
        // button (two same-named controls → ambiguous by-name selection swallowed
        // the create). A specific name also gives this icon-only trigger a name
        // on mobile, where the "Créer" span is hidden. Keeps "Créer" as a
        // substring (WCAG 2.5.3 Label-in-Name).
        aria-label={t('nav.header.createItem')}
        className="flex min-h-[44px] items-center gap-1.5 rounded-token bg-gradient-to-br from-primary to-accent px-2.5 py-1.5 text-sm font-medium text-primary-foreground shadow-elevation-1 transition hover:brightness-110 hover:shadow-elevation-2 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-surface"
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
        <span className="hidden sm:inline">{t('nav.header.create')}</span>
      </button>

      {open && (
        <div
          id={panelId}
          className="absolute right-0 top-full z-50 mt-1 w-60 overflow-hidden rounded-token-lg border border-border bg-surface py-1 shadow-elevation-3"
        >
          <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted">
            {t('nav.header.create')}
          </div>
          <ul>
            {QUICK_CREATE.map((item) => (
              <li key={item.to + item.labelKey}>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    void navigate(item.to);
                  }}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-foreground hover:bg-surface-2 focus:outline-none focus-visible:bg-surface-2 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-token bg-surface-2 text-primary">
                    <NavIcon name={item.icon} className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1 truncate">{t(item.labelKey)}</span>
                  <span className="shrink-0 text-[11px] text-muted">{t(item.hintKey)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function UserMenu({ displayName, onSignout }: { displayName: string; onSignout: () => void }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const panelId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const initials = displayName.trim().charAt(0).toUpperCase() || 'U';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex min-h-[44px] items-center gap-2 rounded-token p-1 pr-2 text-sm text-foreground transition-colors hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-ring"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent text-xs font-semibold text-primary-foreground shadow-elevation-1 ring-1 ring-inset ring-white/20">
          {initials}
        </span>
        <span className="hidden max-w-[10rem] truncate sm:inline">{displayName}</span>
        <svg aria-hidden="true" viewBox="0 0 24 24" className="hidden h-4 w-4 text-muted sm:inline" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          id={panelId}
          className="absolute right-0 top-full z-50 mt-1 w-56 overflow-hidden rounded-token-lg border border-border bg-surface py-1 shadow-elevation-2"
        >
          <div className="border-b border-border px-3 py-2">
            <div className="truncate text-sm font-medium text-foreground">{displayName}</div>
            <div className="text-xs text-muted">{t('nav.user.connectedAccount')}</div>
          </div>
          <ul>
            <li>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  void navigate('/settings/profile');
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-surface-2 focus:outline-none focus-visible:bg-surface-2 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              >
                <NavIcon name="settings" className="h-4 w-4" />
                {t('nav.user.settings')}
              </button>
            </li>
            <li>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onSignout();
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-danger hover:bg-surface-2 focus:outline-none focus-visible:bg-surface-2 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              >
                <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 17l5-5-5-5M15 12H3" />
                </svg>
                {t('nav.user.signout')}
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}

function readEnv(): string {
  try {
    const cfg = getConfig();
    if (cfg.env) return cfg.env;
    if (/localhost|127\.0\.0\.1/.test(cfg.apiBaseUrl)) return 'local';
    return 'prod';
  } catch {
    return 'local';
  }
}

// ---------------------------------------------------------------------------
// Shell.
// ---------------------------------------------------------------------------

type ApiHealth = 'checking' | 'up' | 'down';

/**
 * Poll the gateway /health so the footer status dot reflects reality instead of
 * a hardcoded green "Opérationnel" (BUG-03: it stayed green even during a total
 * backend 503). /health is public (no auth) and CORS-enabled; a network failure
 * or non-2xx (e.g. the nginx 503 when the upstream is down) reads as "down".
 */
function useApiHealth(): ApiHealth {
  const [health, setHealth] = useState<ApiHealth>('checking');
  useEffect(() => {
    let alive = true;
    const check = async () => {
      const ctrl = new AbortController();
      const timer = window.setTimeout(() => ctrl.abort(), 8000);
      try {
        const { apiBaseUrl } = getConfig();
        const res = await fetch(`${apiBaseUrl}/health`, { signal: ctrl.signal });
        if (alive) setHealth(res.ok ? 'up' : 'down');
      } catch {
        if (alive) setHealth('down');
      } finally {
        window.clearTimeout(timer);
      }
    };
    void check();
    const id = window.setInterval(check, 30000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, []);
  return health;
}

/**
 * A single sidebar link. Encapsulates the WCAG-safe active state (full
 * `text-foreground` contrast + the gradient tint & left-bar as the non-colour
 * active cue), an explicit focus ring, a 44px touch target, and — in the
 * collapsed rail — a role="tooltip" label that is hoverable (SC 1.4.13: no
 * `pointer-events-none`) and dismissable with Escape (reset when the pointer /
 * focus leaves). The accessible name is always present via `title` + `aria-label`.
 *
 * E8 additions: a ★ pin button (sibling of the link — never nested interactive
 * content), and a lock badge for plan-gated items. `active` is supplied by the
 * shell (longest-match across the catalog) so a parent route such as `/settings`
 * is not highlighted together with its child `/settings/billing`.
 */
function SidebarItem({
  item,
  collapsed,
  nested = false,
  active,
  locked,
  favorite,
  onToggleFavorite,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  nested?: boolean;
  active: boolean;
  locked: boolean;
  favorite: boolean;
  onToggleFavorite: (to: string) => void;
  onNavigate: () => void;
}) {
  const { t } = useT();
  const label = item.label ?? t(item.labelKey);
  const [tipDismissed, setTipDismissed] = useState(false);

  return (
    <div className="group/item relative">
      <Link
        to={item.to}
        onClick={onNavigate}
        title={collapsed ? label : undefined}
        aria-label={collapsed ? label : undefined}
        aria-current={active ? 'page' : undefined}
        onKeyDown={
          collapsed
            ? (e) => {
                if (e.key === 'Escape') setTipDismissed(true);
              }
            : undefined
        }
        onMouseLeave={collapsed ? () => setTipDismissed(false) : undefined}
        onBlur={collapsed ? () => setTipDismissed(false) : undefined}
        className={`group relative flex min-h-[44px] items-center gap-3 rounded-token px-2 py-2 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring motion-reduce:transition-none ${
          collapsed ? 'justify-center' : nested ? 'pl-9 pr-9' : 'pr-9'
        } ${
          active
            ? 'bg-gradient-to-r from-primary/[0.18] to-accent/[0.06] font-semibold text-foreground shadow-[inset_0_1px_0_0_rgb(var(--color-primary)/0.16)]'
            : 'text-muted hover:bg-surface-2 hover:text-foreground'
        }`}
      >
        {active && (
          <span
            aria-hidden="true"
            className={`absolute left-0 top-1/2 h-5 -translate-y-1/2 rounded-r-full bg-gradient-to-b from-primary to-accent shadow-[0_0_8px_rgb(var(--color-primary)/0.55)] ${
              collapsed ? 'w-0.5' : 'w-1'
            }`}
          />
        )}
        <NavIcon
          name={item.icon}
          className="h-5 w-5 shrink-0 transition-transform duration-200 group-hover:scale-110 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
        />
        {!collapsed && <span className="truncate">{label}</span>}
        {!collapsed && locked && (
          <span
            role="img"
            aria-label={t('shell.app.layoutsDashboardLayout.notIncludedInYourPlan')}
            title={t('shell.app.layoutsDashboardLayout.notIncludedInYourPlan')}
            data-testid="nav-lock"
            className="shrink-0 rounded-full bg-surface-2 px-1.5 py-0.5 text-[10px] leading-none"
          >
            🔒
          </span>
        )}
        {collapsed && (
          <span
            role="tooltip"
            className={`absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded-token border border-border bg-surface px-2 py-1 text-xs text-foreground opacity-0 shadow-elevation-2 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 ${
              tipDismissed ? 'hidden' : ''
            }`}
          >
            {label}
            {locked ? ' 🔒' : ''}
          </span>
        )}
      </Link>
      {!collapsed && (
        <button
          type="button"
          onClick={() => onToggleFavorite(item.to)}
          aria-pressed={favorite}
          aria-label={favorite ? t('shell.app.layoutsDashboardLayout.removeLabelFromFavorites', { label }) : t('shell.app.layoutsDashboardLayout.addLabelToFavorites', { label })}
          title={favorite ? t('shell.app.layoutsDashboardLayout.removeFromFavorites') : t('shell.app.layoutsDashboardLayout.addToFavorites')}
          data-testid="nav-pin"
          className={`absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-token text-muted transition-opacity hover:text-foreground focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring group-hover/item:opacity-100 [@media(hover:none)]:opacity-60 ${
            favorite ? 'opacity-100 text-warning' : 'opacity-0'
          }`}
        >
          <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill={favorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" />
          </svg>
        </button>
      )}
    </div>
  );
}

export function DashboardLayout() {
  const auth = useAuth();
  const { t } = useT();
  // Reconcile the UI language with the signed-in user's saved preference.
  useProfileLocaleSync(auth.isAuthenticated);
  const { sidebarCollapsed, toggleSidebar } = useUIStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const apiHealth = useApiHealth();
  const { isVisible, isLocked } = useNavAccess();
  const favorites = useNavStore((st) => st.favorites);
  const recents = useNavStore((st) => st.recents);
  const toggleFavorite = useNavStore((st) => st.toggleFavorite);
  const pushRecent = useNavStore((st) => st.pushRecent);
  const clearRecents = useNavStore((st) => st.clearRecents);

  // Refs for mobile-drawer focus management (SC 2.4.3).
  const mobileDrawerRef = useRef<HTMLElement>(null);
  const mobileCloseRef = useRef<HTMLButtonElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const mainColRef = useRef<HTMLDivElement>(null);

  // R17 workspace preset (org setting, user-service): `founder` regroups the
  // sidebar into one focused hub + a "More" hub; other presets = full tree.
  const workspacePreset = useWorkspacePresetStore((s) => s.preset);
  const loadWorkspacePreset = useWorkspacePresetStore((s) => s.load);
  useEffect(() => {
    void loadWorkspacePreset();
  }, [loadWorkspacePreset]);
  const presetHubs = applyWorkspacePreset(NAV_HUBS, workspacePreset, {
    founderHub: t('adoption.preset.founderHub'),
    more: t('adoption.preset.moreGroup'),
    groups: {
      home: t('adoption.preset.groups.home'),
      content: t('adoption.preset.groups.content'),
      analytics: t('adoption.preset.groups.analytics'),
      copilot: t('adoption.preset.groups.copilot'),
      settings: t('adoption.preset.groups.settings'),
    },
  });
  const hubKeys = presetHubs.map((h) => h.id);
  const collapsibleKeys =
    presetHubs === NAV_HUBS
      ? ALL_COLLAPSIBLE_KEYS
      : presetHubs.flatMap((h) => [h.id, ...(h.subGroups?.map((s) => s.id) ?? [])]);
  const {
    isOpen: isGroupOpen,
    toggle: toggleGroup,
    open: openGroup,
    allCollapsed,
    setAllCollapsed,
  } = useNavGroupExpansion(collapsibleKeys, hubKeys);

  // Global keyboard layer: `?` opens the cheat-sheet, `/` opens the palette,
  // `g <key>` jumps between sections. (Cmd/Ctrl-K stays owned by the palette.)
  useGlobalShortcuts({
    onHelp: () => setShortcutsOpen(true),
    onOpenPalette: () => setPaletteOpen(true),
  });

  const displayName =
    auth.user?.profile?.name ??
    auth.user?.profile?.preferred_username ??
    t('nav.user.fallbackName');
  const env = readEnv();

  // Header breadcrumb (wayfinding). Derived from the URL — no route-file
  // coupling: the longest-matching top-level section, then the active sub-route
  // name resolved from the command catalog (e.g. CRM ▸ Contacts).
  const { pathname } = useLocation();
  const pageKey = pageKeyOf(useMatches(), pathname);
  // The single hub item that owns the current URL (longest-prefix match) - drives
  // the active highlight, the auto-reveal of its hub, and the Recents tracker.
  const activeTo = matchNavItem(pathname)?.to;
  // Récents track the most specific navigable target — a section-tab route
  // (e.g. /analytics/sentiment) is remembered as itself, not as its hub.
  const recentTo = matchNavItem(pathname, ALL_NAV_TARGETS)?.to;
  useEffect(() => {
    if (recentTo && recentTo !== '/') pushRecent(recentTo);
  }, [recentTo, pushRecent]);

  // Sections come from the real 8-hub catalog (every hub item, incl. the
  // formerly-orphan /automations, /learning and the /settings/*, /ai-team/*
  // leaves), so each of them resolves its own crumb; the leaf resolver merges
  // the static palette commands with the section-tab registry.
  const breadcrumbSections = ALL_HUB_ITEMS.map((item) => ({
    to: item.to,
    label: item.label ?? t(item.labelKey),
  }));
  const crumbs = deriveBreadcrumb(pathname, breadcrumbSections, findCommandLabel);

  // Mobile drawer (SC 2.4.3): on open, move focus to the close button and trap
  // Tab inside the drawer; on close, restore focus to the hamburger and drop
  // `inert` from the backdrop'd main column. Escape closes. Resizing up to the
  // desktop breakpoint also closes it (so `inert` never strands the desktop UI).
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const drawer = mobileDrawerRef.current;
    const main = mainColRef.current;
    main?.setAttribute('inert', '');
    const focusId = window.setTimeout(() => mobileCloseRef.current?.focus(), 0);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
        return;
      }
      if (e.key === 'Tab' && drawer) {
        const target = nextTrapFocus(getFocusable(drawer), document.activeElement, e.shiftKey);
        if (target) {
          e.preventDefault();
          target.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);

    const mq = window.matchMedia('(min-width: 768px)');
    const onViewport = () => {
      if (mq.matches) setMobileOpen(false);
    };
    mq.addEventListener('change', onViewport);

    return () => {
      window.clearTimeout(focusId);
      document.removeEventListener('keydown', onKey, true);
      mq.removeEventListener('change', onViewport);
      main?.removeAttribute('inert');
      hamburgerRef.current?.focus();
    };
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  // Shared chevron for the group / sub-group disclosure buttons.
  const chevron = (open: boolean, alwaysVisible: boolean) => (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={`h-3.5 w-3.5 shrink-0 transition-all duration-200 motion-reduce:transition-none ${
        open ? (alwaysVisible ? '' : 'opacity-0 group-hover/gh:opacity-100 group-focus-visible/gh:opacity-100') : '-rotate-90 opacity-100'
      }`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );

  // Role/plan-aware filtering of the hub tree: admin-only items are removed for
  // non-admins (and a section/hub left empty disappears); plan-locked items stay
  // (badged) so the owner / upgrade path is never hidden.
  const visibleHubs: NavHub[] = presetHubs.map((h): NavHub | null => {
    if (h.subGroups) {
      const subGroups = h.subGroups
        .map((sub) => ({ ...sub, items: sub.items.filter(isVisible) }))
        .filter((sub) => sub.items.length > 0);
      return subGroups.length > 0 ? { ...h, subGroups } : null;
    }
    const items = (h.items ?? []).filter(isVisible);
    return items.length > 0 ? { ...h, items } : null;
  }).filter((h): h is NavHub => h !== null);

  const favoriteItems = favorites
    .map((to) => getNavItem(to))
    .filter((i): i is NavItem => i !== undefined && isVisible(i));
  const recentItems = recents
    .map((to) => getNavItem(to))
    .filter((i): i is NavItem => i !== undefined && isVisible(i) && !favorites.includes(i.to));

  const renderItem = (item: NavItem, collapsed: boolean, nested = false) => (
    <SidebarItem
      item={item}
      collapsed={collapsed}
      nested={nested}
      active={item.to === activeTo}
      locked={isLocked(item)}
      favorite={favorites.includes(item.to)}
      onToggleFavorite={toggleFavorite}
      onNavigate={closeMobile}
    />
  );

  // Pinned / recent shortcut list (expanded rail only).
  const renderShortcutSection = (
    key: string,
    title: string,
    items: NavItem[],
    action?: { label: string; onClick: () => void },
  ) => {
    if (items.length === 0) return null;
    const open = isGroupOpen(key);
    const panelId = `navs-${key}`;
    return (
      <div className="mb-3" data-testid={`nav-${key}`}>
        <div className="flex items-center">
          <button
            type="button"
            onClick={() => toggleGroup(key)}
            aria-expanded={open}
            aria-controls={panelId}
            className="group/gh flex flex-1 items-center justify-between rounded-token px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span>{title}</span>
            {chevron(open, false)}
          </button>
          {action && open && (
            <button
              type="button"
              onClick={action.onClick}
              className="rounded-token px-1.5 py-0.5 text-[11px] text-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {action.label}
            </button>
          )}
        </div>
        <ul id={panelId} className="space-y-0.5">
          {open &&
            items.map((item) => (
              <li key={item.to}>{renderItem(item, false)}</li>
            ))}
        </ul>
      </div>
    );
  };

  const renderNav = (collapsed: boolean) => (
    <nav aria-label={t('nav.primaryNav')} className="flex-1 overflow-y-auto overflow-x-hidden p-2">
      {!collapsed && (
        <div className="mb-1 flex justify-end px-2">
          <button
            type="button"
            onClick={() => setAllCollapsed(!allCollapsed)}
            className="rounded-token px-1.5 py-0.5 text-[11px] text-muted transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={allCollapsed ? t('shell.app.layoutsDashboardLayout.expandAll') : t('shell.app.layoutsDashboardLayout.collapseAll')}
          >
            {allCollapsed ? t('shell.app.layoutsDashboardLayout.expandAll2') : t('shell.app.layoutsDashboardLayout.collapseAll2')}
          </button>
        </div>
      )}
      {!collapsed && renderShortcutSection('sec.favorites', 'Favoris', favoriteItems)}
      {!collapsed &&
        renderShortcutSection('sec.recents', t('shell.app.layoutsDashboardLayout.recent'), recentItems, {
          label: t('shell.app.layoutsDashboardLayout.erase'),
          onClick: clearRecents,
        })}
      {visibleHubs.map((hub) => {
        const hubItems = flattenHubItems(hub);
        const hubHasActive = hubItems.some((i) => i.to === activeTo);

        // Icon rail: ONE icon per hub. Clicking it widens the rail and opens that
        // hub, so every item stays reachable (no dead ends in the collapsed rail).
        if (collapsed) {
          return (
            <div key={hub.id} className="group/item relative mb-1">
              <button
                type="button"
                onClick={() => {
                  openGroup(hub.id);
                  toggleSidebar();
                }}
                aria-label={hub.label}
                title={hub.label}
                aria-current={hubHasActive ? 'true' : undefined}
                className={`group relative flex min-h-[44px] w-full items-center justify-center rounded-token px-2 py-2 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${
                  hubHasActive
                    ? 'bg-gradient-to-r from-primary/[0.18] to-accent/[0.06] text-foreground'
                    : 'text-muted hover:bg-surface-2 hover:text-foreground'
                }`}
              >
                <NavIcon name={hub.icon} className="h-5 w-5 shrink-0" />
                <span
                  role="tooltip"
                  className="absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded-token border border-border bg-surface px-2 py-1 text-xs text-foreground opacity-0 shadow-elevation-2 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                >
                  {hub.label}
                </span>
              </button>
            </div>
          );
        }

        // Collapsed by default (owner rule), the active route's hub included; its
        // header is highlighted instead so the current location stays visible.
        const hubOpen = isGroupOpen(hub.id);
        const hubPanelId = `navg-${hub.id.replace(/\W+/g, '-')}`;
        return (
          <div key={hub.id} className="mb-3" data-testid={`nav-hub-${hub.id}`}>
            <button
              type="button"
              onClick={() => toggleGroup(hub.id)}
              aria-expanded={hubOpen}
              aria-controls={hubPanelId}
              aria-current={hubHasActive && !hubOpen ? 'true' : undefined}
              className={`group/gh flex w-full items-center gap-2 rounded-token px-2 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                hubHasActive ? 'text-foreground' : 'text-muted'
              }`}
            >
              <NavIcon name={hub.icon} className="h-4 w-4 shrink-0" />
              <span className="flex-1 text-left">{hub.label}</span>
              {chevron(hubOpen, false)}
            </button>
            <div id={hubPanelId}>
              {hubOpen &&
                (hub.subGroups
                  ? hub.subGroups.map((sub) => {
                      // APG disclosure-navigation: a nested aria-expanded button
                      // controls each sub-<ul>, keyed by the sub-group id (same
                      // in-memory set as the hubs; collapsed by default).
                      const subOpen = isGroupOpen(sub.id);
                      const subPanelId = `navsg-${sub.id.replace(/\W+/g, '-')}`;
                      return (
                        <div key={sub.id} className="mb-1">
                          <button
                            type="button"
                            onClick={() => toggleGroup(sub.id)}
                            aria-expanded={subOpen}
                            aria-controls={subPanelId}
                            className="group/gh flex min-h-[44px] w-full items-center justify-between rounded-token px-2 py-1.5 pl-3 text-[11px] font-medium uppercase tracking-wide text-muted transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
                          >
                            <span>{sub.label}</span>
                            {chevron(subOpen, true)}
                          </button>
                          <ul id={subPanelId} className="space-y-0.5">
                            {subOpen &&
                              sub.items.map((item) => (
                                <li key={item.to}>{renderItem(item, false, true)}</li>
                              ))}
                          </ul>
                        </div>
                      );
                    })
                  : (hub.items ?? []).map((item) => (
                      <Fragment key={item.to}>{renderItem(item, false)}</Fragment>
                    )))}
            </div>
          </div>
        );
      })}
    </nav>
  );

  const brand = (collapsed: boolean) => (
    <div className="flex h-14 items-center gap-2 border-b border-border px-4">
      <BrandLogo className="h-8 w-8 shrink-0 rounded-token shadow-elevation-1 ring-1 ring-inset ring-white/20" />
      {!collapsed && (
        <span className="bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-base font-bold tracking-tight text-transparent">
          {t('nav.brand')}
        </span>
      )}
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-bg text-foreground">
      {/* Skip link (SC 2.4.1): the first focusable element in the shell,
          visually hidden until focused, jumping past the nav to <main>. Label is
          a literal (the nav i18n catalog is owned outside this change's scope). */}
      <a
        href="#main-content"
        className="sr-only z-[10001] rounded-token bg-surface px-4 py-2 text-sm font-medium text-foreground shadow-elevation-3 ring-2 ring-ring focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:outline-none"
      >{t('shell.app.layoutsDashboardLayout.skipToMainContent')}</a>

      {/* Desktop rail */}
      <aside
        aria-label={t('nav.primaryNav')}
        className={`hidden shrink-0 flex-col border-r border-border bg-surface transition-all motion-reduce:transition-none md:flex ${
          sidebarCollapsed ? 'w-16' : 'w-60'
        }`}
      >
        {brand(sidebarCollapsed)}
        {renderNav(sidebarCollapsed)}
      </aside>

      {/* Mobile drawer + backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-950/50 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        ref={mobileDrawerRef}
        id="primary-navigation-drawer"
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-border bg-surface shadow-elevation-3 transition-transform motion-reduce:transition-none md:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label={t('nav.primaryNav')}
        aria-hidden={!mobileOpen}
      >
        <div className="flex h-14 items-center justify-between border-b border-border px-4">
          <span className="flex items-center gap-2">
            <BrandLogo className="h-8 w-8 shrink-0 rounded-token shadow-elevation-1 ring-1 ring-inset ring-white/20" />
            <span className="text-base font-bold tracking-tight text-foreground">{t('nav.brand')}</span>
          </span>
          <button
            ref={mobileCloseRef}
            type="button"
            onClick={() => setMobileOpen(false)}
            aria-label={t('nav.header.closeMenu')}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-token p-2 text-muted hover:bg-surface-2 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        {renderNav(false)}
      </aside>

      {/* Main column. `inert` is toggled imperatively (see the mobile-drawer
          effect) so the backdrop'd content leaves the tab order + a11y tree
          while the drawer is open. */}
      <div ref={mainColRef} className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="um-glass relative z-20 flex h-14 shrink-0 items-center justify-between gap-2 border-b border-border px-3 shadow-elevation-1 sm:px-4">
          <div className="flex min-w-0 items-center gap-1">
            {/* Mobile: open drawer */}
            <button
              ref={hamburgerRef}
              type="button"
              onClick={() => setMobileOpen(true)}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-token p-2 text-muted hover:bg-surface-2 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring md:hidden"
              aria-label={t('nav.header.openMenu')}
              aria-expanded={mobileOpen}
              aria-controls="primary-navigation-drawer"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            {/* Desktop: collapse rail */}
            <button
              type="button"
              onClick={toggleSidebar}
              className="hidden min-h-[44px] min-w-[44px] items-center justify-center rounded-token p-2 text-muted hover:bg-surface-2 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring md:inline-flex"
              aria-label={t('nav.header.toggleSidebar')}
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* Breadcrumb (derived from the URL): root ▸ section ▸ active sub-route. */}
            {crumbs.length > 0 && (
              <nav aria-label={t('nav.header.breadcrumbAria')} className="ml-1 hidden min-w-0 items-center md:flex">
                <span className="shrink-0 text-sm text-muted">{t('nav.header.breadcrumbRoot')}</span>
                {crumbs.map((crumb, i) => (
                  <Fragment key={`${crumb.label}-${i}`}>
                    <svg aria-hidden="true" viewBox="0 0 24 24" className="mx-1 h-4 w-4 shrink-0 text-muted/60" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m9 6 6 6-6 6" />
                    </svg>
                    <span
                      aria-current={crumb.isCurrent ? 'page' : undefined}
                      className={`truncate text-sm ${
                        crumb.isCurrent ? 'font-semibold text-foreground' : 'text-muted'
                      }`}
                    >
                      {crumb.label}
                    </span>
                  </Fragment>
                ))}
              </nav>
            )}
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="flex min-h-[44px] items-center gap-2 rounded-token border border-border bg-surface-2/40 px-2 py-1.5 text-sm text-muted transition-colors hover:border-primary/40 hover:bg-surface-2 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring lg:min-w-[13rem]"
              aria-label={t('nav.header.openCommandPalette')}
              aria-keyshortcuts="Control+K Meta+K /"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.2-3.2" />
              </svg>
              <span className="hidden lg:inline">{t('nav.header.searchPlaceholder')}</span>
              <kbd className="ml-auto hidden rounded border border-border bg-surface px-1.5 py-0.5 text-[10px] font-medium text-foreground lg:inline">
                ⌘K
              </kbd>
            </button>

            <WorkspaceSwitcher />

            <AccountSwitcher />

            <QuickCreateMenu />

            <button
              type="button"
              onClick={() => setShortcutsOpen(true)}
              aria-label={t('nav.header.showShortcuts')}
              aria-keyshortcuts="?"
              title={`${t('nav.header.shortcuts')} (?)`}
              className="hidden min-h-[44px] min-w-[44px] items-center justify-center rounded-token p-2 text-muted transition-colors hover:bg-surface-2 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:inline-flex"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .7-1 1.4v.3" />
                <path d="M12 17h.01" />
              </svg>
            </button>

            <AiCreditsBadge />

            <SupportButton />

            <NotificationBell />

            <LanguageSwitcher className="hidden sm:block" />

            <ThemePicker />

            <UserMenu
              displayName={displayName}
              onSignout={() => {
                // Drop any delegated scope so the next session starts in the
                // user's OWN account (Loi-25: scope never outlives the session).
                clearActingTenant();
                void auth.signoutRedirect();
              }}
            />
          </div>
        </header>

        <ActingTenantBanner />

        <UpdateBanner />

        <main id="main-content" tabIndex={-1} className="flex-1 overflow-y-auto overflow-x-hidden focus:outline-none">
          {/* Keyed by page (the pathname; a RouteModal child keeps its parent's
              key so the list under the modal stays mounted) so a gentle fade/rise
              plays on every route change (CSS handles reduced-motion). */}
          <div key={pageKey} className="um-animate-page min-h-full p-4 pb-16 sm:p-6 sm:pb-16">
            {/* Keyed by pathname (parent div) so a page crash resets on nav.
                Contains render errors to this panel — the shell stays alive. */}
            <RouteErrorBoundary>
              <Outlet />
            </RouteErrorBoundary>
          </div>
        </main>

        <footer className="um-glass flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-border px-4 py-2 text-xs text-muted">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-medium text-foreground">{t('nav.brand')}</span>
            <span aria-hidden="true">·</span>
            <Link
              to="/versionsbuilt"
              className="rounded-token-sm text-muted transition-colors hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              title={t('shell.app.layoutsDashboardLayout.buildProvenanceVersionCommitDate')}
            >
              v{APP_VERSION}
            </Link>
            <span
              className="rounded-full bg-surface-2 px-1.5 py-0.5 font-medium uppercase tracking-wide"
              title={t('nav.footer.environment')}
            >
              {env}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <nav aria-label={t('nav.footer.links')} className="flex items-center gap-x-4">
              <Link
                to="/versionsbuilt"
                className="rounded-token-sm transition-colors hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {t('nav.footer.whatsNew')}
              </Link>
              <button
                type="button"
                onClick={() => setShortcutsOpen(true)}
                className="rounded-token-sm transition-colors hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {t('nav.footer.shortcuts')}
              </button>
              <Link
                to="/confidentialite"
                className="rounded-token-sm transition-colors hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {t('nav.footer.privacy')}
              </Link>
            </nav>
            <span
              className="flex items-center gap-1.5"
              title={t('nav.footer.statusTitle')}
            >
              <span className="relative flex h-2 w-2" aria-hidden="true">
                {apiHealth === 'up' && (
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60 motion-reduce:hidden" />
                )}
                <span
                  className={`relative inline-flex h-2 w-2 rounded-full ${
                    apiHealth === 'up'
                      ? 'bg-success'
                      : apiHealth === 'down'
                        ? 'bg-danger'
                        : 'bg-warning'
                  }`}
                />
              </span>
              <span>
                {apiHealth === 'up'
                  ? t('nav.footer.operational')
                  : apiHealth === 'down'
                    ? t('nav.footer.unavailable')
                    : t('nav.footer.checking')}
              </span>
            </span>
          </div>
        </footer>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <ShortcutsOverlay open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      <Toaster />
      <GlobalConfirmHost />
      <CopilotProvider />
      <CreditGuardHost />
      {/* NEW sibling to CreditGuardHost (not an edit to it): a soft follow-up
          offering the detailed "request more credits" modal after a 402. */}
      <OutOfCreditsRequestMoreHost />
      {/* S8 — upgrade dialog on any server 403 feature_not_in_plan / plan_required. */}
      <PlanRequiredHost />
      <DemoTour />
      <OnboardingGate />
    </div>
  );
}
